import axios from "axios";
import { IUser, loginObject } from "@@/types";
import { SavedCompaniesRepository } from "~/repositories/SavedCompaniesRepository";
import { SavedJobsRepository } from "~/repositories/SavedJobsRepository";
import { ApplicationsRepository } from "~/repositories/ApplicationsRepository";

const user = ref<IUser>(null);
const lovedCompanyIds = ref<number[]>([]);
const savedJobIds = ref<number[]>([]);
const applicationIds = ref<number[]>([]);
let bearerToken = "";
let xsrfToken = "";

const updatingUser = ref(false);

// Cookies expires date - 2 hours
const expiresDate = new Date(Date.now() + 4 * 60 * 60 * 1000);

// The API authenticates with the bearer token only (see CSRF-exempt routes / Nitro proxies),
// so the bearer token is the only thing the logged-in state depends on. XSRF-TOKEN is still read
// because the Nitro handlers forward it, but a missing XSRF/session cookie must not log the user out.
// The in-memory token counts too: it survives the `token` cookie expiring or being cleared while the page is open.
function hasRequiredAuthCookies(): boolean {
	xsrfToken = useCookie("XSRF-TOKEN").value || "";

	return !!(bearerToken || useCookie("token").value);
}

function waitForAuthCookies(maxAttempts = 300, interval = 100): Promise<void> {
	return new Promise((resolve, reject) => {
		let attempts = 0;

		const checkInterval = setInterval(() => {
			attempts++;

			if (hasRequiredAuthCookies()) {
				clearInterval(checkInterval);
				resolve();
			} else if (attempts >= maxAttempts) {
				clearInterval(checkInterval);
				reject(new Error("Required authentication cookies are missing."));
			}
		}, interval);
	});
}

function getUser() {
	return user.value;
}

function getToken() {
	return bearerToken;
}

function getXsrfToken() {
	return xsrfToken;
}

function isUpdatingUser() {
	return updatingUser.value;
}

function extractTokenFromResponse(response: unknown): string {
	return response?.[0]?.token || response?.token || "";
}

function setTokenCookie(token: string) {
	if (!token) {
		return;
	}

	const tokenCookie = useCookie("token", { expires: expiresDate });
	tokenCookie.value = token;
	bearerToken = token;
}

async function syncAuthenticatedUser(token?: string) {
	const resolvedToken = token || bearerToken || useCookie("token").value || "";

	if (!resolvedToken) {
		throw new Error("Authentication token cookie is missing.");
	}

	const response = await fetchUserWithRetry(resolvedToken);
	setTokenCookie(resolvedToken);
	user.value = response.data;
	useState("isAuth").value = true;

	return response.data;
}

function checkCookies() {
	const app = useNuxtApp();

	app.hook("app:mounted", () => {
		waitForAuthCookies()
			.then(() => {
				const tokenCookie = useCookie("token");

				syncAuthenticatedUser(tokenCookie.value || "")
					.then(() => {
						fetchUserSideData();
					})
					.catch((error) => {
						// Only an explicit 401 means the token is invalid. Transient failures
						// (network, 5xx, 429) keep the cookies so the next load can recover.
						if (error?.response?.status === 401) {
							clearUserData();
						}
					});
			})
			.catch(() => {
				clearUserData();
			});
	});

	app.hook("page:start", () => {
		waitForAuthCookies()
			.then(() => {
				// Many components read the bearer from the cookie, so restore it from memory if it got lost.
				if (bearerToken && !useCookie("token").value) {
					setTokenCookie(bearerToken);
				}
			})
			.catch(() => {
				clearUserData();
			});
	});
}

function setCookies(response) {
	const cookieHeaders = Array.isArray(response?.[0]) ? response[0] : Array.isArray(response) ? response : [];

	cookieHeaders.forEach((value) => {
		const cookieParts = value.split("=");
		if (cookieParts[0] == "dreamjobs_session" || cookieParts[0] == "XSRF-TOKEN") {
			const cookie = useCookie(cookieParts[0], {
				expires: expiresDate,
			});
			cookie.value = cookieParts[1].split(";")[0];
			if (cookieParts[0] == "XSRF-TOKEN") xsrfToken = cookieParts[1].split(";")[0];
		}
	});
}

async function fetchUser(token: string) {
	const { locale } = useNuxtApp().$i18n;
	const url = `${globalConfig.API}/profile?locale=${locale.value}`;

	return await axios.get(url, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});
}

const PROFILE_FETCH_ATTEMPTS = 3;
const PROFILE_FETCH_RETRY_DELAY = 500;

function isRetryableProfileError(error: { response?: { status?: number } }): boolean {
	const status = error?.response?.status;

	// No response (network error / timeout), rate limit or server error.
	return !status || status === 429 || status >= 500;
}

/** Fetch the profile, retrying transient failures. A 401 (or any other 4xx) is thrown immediately. */
async function fetchUserWithRetry(token: string) {
	for (let attempt = 1; ; attempt++) {
		try {
			return await fetchUser(token);
		} catch (error) {
			if (
				attempt >= PROFILE_FETCH_ATTEMPTS ||
				!isRetryableProfileError(error as { response?: { status?: number } })
			) {
				throw error;
			}

			await new Promise((resolve) => setTimeout(resolve, PROFILE_FETCH_RETRY_DELAY * attempt));
		}
	}
}

async function fetchLovedCompanyIds() {
	try {
		const result = await new SavedCompaniesRepository().getLovedCompanyIds();
		lovedCompanyIds.value = Array.isArray(result) ? result : [];
	} catch (error) {
		console.error("fetchLovedCompanyIds");
	}
}

async function fetchSavedJobIds() {
	try {
		const result = await new SavedJobsRepository().getSavedJobIds();
		savedJobIds.value = Array.isArray(result) ? result : [];
	} catch (error) {
		console.error("fetchSavedJobIds");
	}
}

async function fetchApplicationIds() {
	try {
		const result = await new ApplicationsRepository().getApplicationIds();
		applicationIds.value = Array.isArray(result) ? result : [];
	} catch (error) {
		console.error("fetchApplicationIds");
		applicationIds.value = [];
	}
}

function fetchUserSideData() {
	fetchLovedCompanyIds();
	fetchSavedJobIds();
	fetchApplicationIds();
}

/** Refresh the authenticated user. Returns true on success; never throws. */
async function updateUser(): Promise<boolean> {
	updatingUser.value = true;
	try {
		const response = await fetchUser(bearerToken);
		user.value = response.data;
		updatingUser.value = false;
		fetchUserSideData();
		return true;
	} catch (error) {
		console.error("updateUser", error);
		updatingUser.value = false;
		return false;
	}
}

async function authenticateWithOneTimeToken(token: string) {
	const oneTimeTokenState = useState("oneTimeTokenPending", () => false);
	oneTimeTokenState.value = true;

	try {
		const csrfResponse = await $fetch("/api/csrf", {
			method: "POST",
		});

		setCookies(csrfResponse);

		if (!xsrfToken) {
			throw new Error("Failed to fetch CSRF token.");
		}

		const response = await $fetch("/api/auth/one-time-token-login", {
			method: "POST",
			body: {
				token,
				xsrf: xsrfToken,
			},
		});

		const responseToken = extractTokenFromResponse(response);
		setTokenCookie(responseToken);

		try {
			await waitForAuthCookies(50, 100);
		} catch {
			if (!responseToken && !useCookie("token").value) {
				throw new Error("Authentication cookies were not set after one-time token login.");
			}
		}

		return await syncAuthenticatedUser(responseToken || useCookie("token").value || "");
	} catch (error) {
		clearUserData();
		throw error;
	} finally {
		oneTimeTokenState.value = false;
	}
}

function login(loginData: loginObject) {
	return new Promise(async (resolve, reject) => {
		// Get CSRF token
		await $fetch("/api/csrf", {
			method: "POST",
		})
			.then(async (response) => {
				// Set cookies
				setCookies(response);
				if (xsrfToken == "" || xsrfToken == undefined) reject();

				// Login
				await $fetch("/api/login", {
					method: "POST",
					body: {
						...loginData,
						xsrf: xsrfToken,
					},
				})
					.then((response) => {
						// Bearer token
						const token = response[0].token;

						// Set token cookie
						const tokenCookie = useCookie("token", { expires: expiresDate });
						tokenCookie.value = token;
						bearerToken = token;

						// Get user profile
						fetchUser(token)
							.then((response) => {
								user.value = response.data;
								useState("isAuth").value = true;
								fetchUserSideData();
								resolve("");
							})
							.catch((error) => {
								reject(error);
							});
					})
					.catch((error) => {
						reject(error);
					});
			})
			.catch((error) => {
				reject(error);
			});
	});
}

function socialLogin(token: string) {
	return new Promise(async (resolve, reject) => {
		// Get CSRF token
		await $fetch("/api/csrf", {
			method: "POST",
		})
			.then(async (response) => {
				// Set cookies
				setCookies(response);
				if (xsrfToken == "" || xsrfToken == undefined) reject();

				// Set token cookie
				const tokenCookie = useCookie("token", { expires: expiresDate });
				tokenCookie.value = token;
				bearerToken = token;

				// Get user profile
				fetchUser(token).then((res) => {
					user.value = res.data;
					useState("isAuth").value = true;
					fetchUserSideData();
					resolve("");
				});
			})
			.catch((error) => {
				reject(error);
			});
	});
}

function clearUserData() {
	user.value = null;
	lovedCompanyIds.value = [];
	savedJobIds.value = [];
	applicationIds.value = [];
	bearerToken = "";
	useState("isAuth").value = false;

	useCookie("XSRF-TOKEN").value = null;
	useCookie("dreamjobs_session").value = null;
	useCookie("token").value = null;
}

function register(registerData: Record<string, unknown>) {
	return new Promise(async (resolve, reject) => {
		// Get CSRF token
		await $fetch("/api/csrf", {
			method: "POST",
		})
			.then(async (response) => {
				// Set cookies
				setCookies(response);

				if (xsrfToken == "" || xsrfToken == undefined) reject();

				// Register
				await $fetch("/api/register", {
					method: "POST",
					body: {
						...registerData,
						xsrf: xsrfToken,
					},
				})
					.then(async (response) => {
						resolve(response);
					})
					.catch((error) => {
						reject(error);
					});
			})
			.catch((error) => {
				reject(error);
			});
	});
}

function requestResetPassword(requestResetData: Record<string, unknown>) {
	return new Promise(async (resolve, reject) => {
		// Get CSRF token
		await $fetch("/api/csrf", {
			method: "POST",
		})
			.then(async (response) => {
				// Set cookies
				setCookies(response);

				if (xsrfToken == "" || xsrfToken == undefined) reject();

				// Forgot password
				await $fetch("/api/forgot-password", {
					method: "POST",
					body: {
						...requestResetData,
						xsrf: xsrfToken,
					},
				})
					.then(async (response) => {
						resolve(response);
					})
					.catch((error) => {
						resolve(error);
					});
			})
			.catch((error) => {
				resolve(error);
			});
	});
}

function resetPassword(resetData: Record<string, unknown>) {
	return new Promise(async (resolve, reject) => {
		// Get CSRF token
		await $fetch("/api/csrf", {
			method: "POST",
		})
			.then(async (response) => {
				// Set cookies
				setCookies(response);

				if (xsrfToken == "" || xsrfToken == undefined) reject();

				// Reset password
				await $fetch("/api/reset-password", {
					method: "POST",
					body: {
						...resetData,
						xsrf: xsrfToken,
					},
				})
					.then(async (response) => {
						resolve(response);
					})
					.catch((error) => {
						reject(error);
					});
			})
			.catch((error) => {
				reject(error);
			});
	});
}

function logout() {
	const router = useRouter();
	const { locale } = useNuxtApp().$i18n;

	clearUserData();

	router.push(`/${locale.value}`).then(() => {
		window.scroll({ top: 0, left: 0, behavior: "smooth" });
	});
}

function isLovedCompany(companyId: number): boolean {
	return lovedCompanyIds.value.includes(companyId);
}

function isSavedJob(jobId: number): boolean {
	return savedJobIds.value.includes(jobId);
}

function isApplied(jobId: number): boolean {
	return Array.isArray(applicationIds.value) && applicationIds.value.includes(jobId);
}

function getApplicationCount(): number {
	return Array.isArray(applicationIds.value) ? applicationIds.value.length : 0;
}

function storeSavedCompany(companyId: number) {
	const isAuth = useState("isAuth", () => false);

	if (!isAuth.value) {
		throw "Not authenticated user";
	}

	$fetch(`${globalConfig.API}/companies/${companyId}/save-company-to-user`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${useCookie("token").value}`,
			"x-xsrf-token": getXsrfToken(),
		},
		body: {},
	})
		.then(() => {
			// GTM tracking
			const { gtm } = useGTM();
			gtm?.trackEvent({ event: "b2c-company-follow" });

			updateUser();
		})
		.catch((error) => {
			console.error("Card storeSavedCompany", error);
		});
}

function requestOtp(otpData: Record<string, unknown>) {
	return new Promise(async (resolve, reject) => {
		await $fetch("/api/csrf", {
			method: "POST",
		})
			.then(async (response) => {
				setCookies(response);
				if (xsrfToken == "" || xsrfToken == undefined) reject();

				await $fetch("/api/otp-request", {
					method: "POST",
					body: {
						...otpData,
						xsrf: xsrfToken,
					},
				})
					.then(async (response) => {
						resolve(response);
					})
					.catch((error) => {
						reject(error);
					});
			})
			.catch((error) => {
				reject(error);
			});
	});
}

function verifyOtp(verifyData: Record<string, unknown>) {
	return new Promise(async (resolve, reject) => {
		await $fetch("/api/csrf", {
			method: "POST",
		})
			.then(async (response) => {
				setCookies(response);
				if (xsrfToken == "" || xsrfToken == undefined) reject();

				await $fetch(`/api/otp-verify`, {
					method: "POST",
					body: {
						...verifyData,
						xsrf: xsrfToken,
					},
				})
					.then((response: unknown) => {
						const token = response[0]?.token || response.token;

						const tokenCookie = useCookie("token", { expires: expiresDate });
						tokenCookie.value = token;
						bearerToken = token;

						fetchUser(token)
							.then((res) => {
								user.value = res.data;
								useState("isAuth").value = true;
								fetchUserSideData();
								resolve(true);
							})
							.catch((error) => {
								reject(error);
							});
					})
					.catch((error) => {
						reject(error);
					});
			})
			.catch((error) => {
				reject(error);
			});
	});
}

export default {
	authenticateWithOneTimeToken,
	login,
	logout,
	socialLogin,
	getUser,
	user,
	getToken,
	getXsrfToken,
	checkCookies,
	updateUser,
	resetPassword,
	register,
	requestResetPassword,
	isUpdatingUser,
	isLovedCompany,
	isSavedJob,
	isApplied,
	getApplicationCount,
	storeSavedCompany,
	requestOtp,
	verifyOtp,
	refreshSavedJobIds: fetchSavedJobIds,
	refreshLovedCompanyIds: fetchLovedCompanyIds,
	savedJobIds,
};
